import React, { useState } from 'react';
import { X, ShieldCheck, Copy, Check, Globe, Mail, Key, ExternalLink, HelpCircle, AlertTriangle } from 'lucide-react';

interface DomainAuthorizationGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorDomain?: string;
  providerName?: string;
}

export const DomainAuthorizationGuideModal: React.FC<DomainAuthorizationGuideModalProps> = ({
  isOpen,
  onClose,
  errorDomain = window.location.hostname,
  providerName = 'Apple ID / Google / Facebook',
}) => {
  const [copiedDomain, setCopiedDomain] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'domains' | 'sender_email' | 'oauth_keys'>('domains');

  if (!isOpen) return null;

  const currentHost = window.location.hostname;
  const currentOrigin = window.location.origin;

  const domainsToAuthorize = [
    'playgroundleague.pro',
    'www.playgroundleague.pro',
    currentHost,
    'ais-dev-yjr4kq4rcqu52dkms2hauo-369133220505.us-east1.run.app',
    'ais-pre-yjr4kq4rcqu52dkms2hauo-369133220505.us-east1.run.app',
    'localhost',
  ].filter((v, i, a) => a.indexOf(v) === i); // unique

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedDomain(text);
    setTimeout(() => setCopiedDomain(null), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-indigo-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-indigo-900/95 text-white rounded-[2.5rem] border border-lime-400/30 shadow-2xl w-full max-w-2xl overflow-hidden p-6 space-y-6 relative my-8">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-indigo-200 hover:text-white rounded-full bg-indigo-950/80 border border-white/10 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center space-x-2 bg-amber-400/20 border border-amber-400/40 px-3 py-1 rounded-full text-xs font-black italic uppercase text-amber-300">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span>Firebase OAuth & Domain Setup Guide</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-black italic uppercase tracking-tight text-white">
            Domain Authorization & Login Options
          </h2>
          <p className="text-xs text-indigo-200/90 font-medium max-w-lg mx-auto leading-relaxed">
            Firebase Security requires domain whitelisting before Apple ID, Google, or Facebook OAuth popup logins can execute. Register <span className="text-lime-300 font-bold">{errorDomain}</span> below or use an alternative login method.
          </p>
        </div>

        {/* Instant Alternative Login Banner */}
        <div className="p-4 bg-lime-400/15 border border-lime-400/40 rounded-2xl space-y-2 text-xs">
          <span className="font-black text-lime-300 uppercase tracking-wider block text-[11px]">
            💡 Need Immediate Access? Alternative Login Methods:
          </span>
          <p className="text-indigo-100 text-[11px]">
            While registering domains in Firebase Console, you can log in instantly using:
          </p>
          <ul className="list-disc pl-4 space-y-1 text-white font-semibold text-[11px]">
            <li><strong>Email & Password Sign-In</strong>: Register a profile or log in with your email address directly.</li>
            <li><strong>Quick Athlete Access</strong>: Click any athlete demo profile on the sign-in page for instant one-click login.</li>
          </ul>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-indigo-950 p-1.5 rounded-2xl border border-white/10 text-xs font-bold">
          <button
            onClick={() => setActiveTab('domains')}
            className={`flex-1 py-2.5 rounded-xl transition flex items-center justify-center space-x-1.5 ${
              activeTab === 'domains' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200 hover:text-white'
            }`}
          >
            <Globe className="w-4 h-4" />
            <span>1. Authorized Domains</span>
          </button>
          <button
            onClick={() => setActiveTab('sender_email')}
            className={`flex-1 py-2.5 rounded-xl transition flex items-center justify-center space-x-1.5 ${
              activeTab === 'sender_email' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200 hover:text-white'
            }`}
          >
            <Mail className="w-4 h-4" />
            <span>2. GoDaddy Sender Email</span>
          </button>
          <button
            onClick={() => setActiveTab('oauth_keys')}
            className={`flex-1 py-2.5 rounded-xl transition flex items-center justify-center space-x-1.5 ${
              activeTab === 'oauth_keys' ? 'bg-lime-400 text-black font-black' : 'text-indigo-200 hover:text-white'
            }`}
          >
            <Key className="w-4 h-4" />
            <span>3. Provider Keys</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'domains' && (
          <div className="space-y-4 animate-fadeIn">
            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-2">
              <h4 className="font-extrabold text-sm text-lime-400 flex items-center">
                <Globe className="w-4 h-4 mr-2" />
                Step-by-Step Domain Whitelisting in Firebase Console
              </h4>
              <ol className="text-xs text-indigo-200 space-y-1.5 list-decimal pl-4 font-medium leading-relaxed">
                <li>
                  Open the <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer" className="text-lime-300 underline font-bold inline-flex items-center">Firebase Console <ExternalLink className="w-3 h-3 ml-0.5" /></a> and select project <strong className="text-white">ai-studio-playgroundleague</strong>.
                </li>
                <li>Go to <strong>Authentication</strong> → <strong>Settings</strong> tab → <strong>Authorized domains</strong>.</li>
                <li>Click <strong>Add Domain</strong> and paste each domain below:</li>
              </ol>
            </div>

            <div className="space-y-2">
              <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider block">Domains to Copy & Add:</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {domainsToAuthorize.map((dom) => (
                  <div
                    key={dom}
                    className="flex items-center justify-between p-2.5 bg-indigo-950 rounded-xl border border-white/10 text-xs font-mono text-lime-300"
                  >
                    <span className="truncate mr-2">{dom}</span>
                    <button
                      onClick={() => handleCopy(dom)}
                      className="px-2.5 py-1 bg-indigo-900 hover:bg-indigo-800 text-white rounded-lg text-[10px] font-sans font-bold flex items-center space-x-1 shrink-0 transition"
                    >
                      {copiedDomain === dom ? (
                        <>
                          <Check className="w-3 h-3 text-lime-400" />
                          <span className="text-lime-400">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sender_email' && (
          <div className="space-y-4 animate-fadeIn">
            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-2 text-xs text-indigo-200 leading-relaxed">
              <h4 className="font-extrabold text-sm text-lime-400 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                Connecting "support@playgroundleague.pro" with GoDaddy & Firebase
              </h4>
              <p>
                To make sure password reset emails and registration confirmations come from <strong className="text-lime-300">support@playgroundleague.pro</strong>:
              </p>

              <div className="p-3 bg-black/40 rounded-xl space-y-2 font-mono text-[11px] text-indigo-100">
                <p>1. Open <strong>Firebase Console</strong> → <strong>Authentication</strong> → <strong>Templates</strong>.</p>
                <p>2. Select <strong>Password reset</strong> or <strong>Email address verification</strong>.</p>
                <p>3. Click <strong>Edit template</strong> → <strong>Sender email</strong> → Click <strong>Customize domain</strong>.</p>
                <p>4. Enter <strong>support@playgroundleague.pro</strong> as your official sender address.</p>
              </div>

              <div className="p-3 bg-indigo-900/60 rounded-xl border border-white/10 space-y-1">
                <strong className="text-white block font-bold">GoDaddy DNS Record Checklist:</strong>
                <p className="text-[11px] text-indigo-200/90 font-mono">
                  • TXT SPF: <span className="text-lime-300">v=spf1 include:_spf.google.com ~all</span><br />
                  • MX Record: Points to GoDaddy email server (<span className="text-lime-300">mailstore1.secureserver.net</span>)
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'oauth_keys' && (
          <div className="space-y-4 animate-fadeIn text-xs text-indigo-200">
            <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-3">
              <h4 className="font-extrabold text-sm text-lime-400 flex items-center">
                <Key className="w-4 h-4 mr-2" />
                Enabling Apple ID, Google, & Facebook Sign-In Providers
              </h4>

              <div className="space-y-2">
                <div className="p-3 bg-black/40 rounded-xl border border-white/10 space-y-1">
                  <span className="font-black text-white uppercase text-[11px] flex items-center">
                     Apple ID OAuth Configuration
                  </span>
                  <p className="text-[11px] text-indigo-200/80">
                    In Firebase Console → Sign-in method → <strong>Apple</strong> → Enable → Enter Apple Developer Services ID (<code className="text-lime-300">pro.playgroundleague.auth</code>), Team ID, Key ID, and Private Key.
                  </p>
                </div>

                <div className="p-3 bg-black/40 rounded-xl border border-white/10 space-y-1">
                  <span className="font-black text-white uppercase text-[11px] flex items-center">
                    🌐 Google OAuth Configuration
                  </span>
                  <p className="text-[11px] text-indigo-200/80">
                    In Firebase Console → Sign-in method → <strong>Google</strong> → Enable → Set support email to <strong className="text-lime-300">support@playgroundleague.pro</strong>.
                  </p>
                </div>

                <div className="p-3 bg-black/40 rounded-xl border border-white/10 space-y-1">
                  <span className="font-black text-white uppercase text-[11px] flex items-center">
                    📘 Facebook OAuth Configuration
                  </span>
                  <p className="text-[11px] text-indigo-200/80">
                    In Firebase Console → Sign-in method → <strong>Facebook</strong> → Enable → Enter Facebook App ID and App Secret from Meta for Developers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-2 border-t border-white/10 flex items-center justify-between text-xs">
          <span className="text-indigo-200/70 font-semibold">
            Need manual assistance? Contact <strong className="text-lime-300">support@playgroundleague.pro</strong>
          </span>
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase rounded-xl transition shadow-md"
          >
            Got It, Close Guide
          </button>
        </div>
      </div>
    </div>
  );
};
